import { useNavigate } from 'react-router-dom'
import { Row, Col } from 'react-bootstrap'
import { FormTypeEnum, IDealerChangedEvent, IRootState } from 'types'
import { FormInput, PageHeader, ReturnButton, FormDealerComboBox, AddButton, DefaultButton } from 'components'
import { useEffect, useRef, useState } from 'react'
import { FormElement, Field, Form, FormClassComponent, } from '@progress/kendo-react-form'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'state/store'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'
import toast from 'core/toast'
import { WebLeadFilterDto, WebLeadFilterDtoApiResponseDto } from 'generated/v1/v1IntegrationsApi'
import DealerSelect from 'components/Dealers/DealerSelect'

interface IProps {
    title: string
    formType: FormTypeEnum
    AddOrUpdateWebLeadFilter: (webLeadFilter: WebLeadFilterDto) => Promise<WebLeadFilterDtoApiResponseDto>
    initialValues?: WebLeadFilterDto
}

const WebsiteLeadsFilterForm = ({ title, formType, initialValues, AddOrUpdateWebLeadFilter }: IProps) => {

    const formRef = useRef<FormClassComponent>(null)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const loading = useSelector((state: IRootState) => state.app.loading)
    const [selectedDealerId, setSelectedDealerId] = useState<number>()

    const redirectUrl = '/integrations/website-leads-filter'

    const isValidForm = ({ firstName, lastName, phone, mobilePhone, email, ipAddress }: WebLeadFilterDto) => {
        return firstName?.trim() || lastName?.trim() || phone?.trim() || mobilePhone?.trim() || email?.trim() || ipAddress?.trim()
    }

    const handleDealerClear = async () => {
        setSelectedDealerId(undefined)
    }

    const handleDealerChange = async (e: IDealerChangedEvent) => {
        setSelectedDealerId(e.dealerId)
    }

    useEffect(() => {
        if (initialValues)
            setSelectedDealerId(initialValues.dealerId)
    }, [])

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSubmit = async (webLeadFilter: any) => {
        try {
            if (!isValidForm(webLeadFilter)) {
                toast.error('Please provide at least one piece of information')
                return
            }

            await dispatch(startLoadingAction())

            let isAddRequest = formType == FormTypeEnum.Add

            let filterValue: WebLeadFilterDto = ({ ...webLeadFilter, dealerId: selectedDealerId })
            let result = await AddOrUpdateWebLeadFilter(filterValue)

            if (result.success) {
                toast.success(`Web Leads Filter ${isAddRequest ? 'created' : 'updated'} successfully`)
                navigate(redirectUrl)
            }
            else {
                toast.responseErrors(result.errors)
            }

        } catch (e) {
            toast.responseErrors(e)
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    return (
        <div id={`${formType == FormTypeEnum.Add ? 'WebsiteLeadsFilterAddPage' : 'WebsiteLeadsFilterEditPage'}`}>
            <PageHeader title={title}>
                <ReturnButton redirectUrl={redirectUrl} />
            </PageHeader>

            <Form
                onSubmit={(e) => handleSubmit(e as WebLeadFilterDto)}
                ref={formRef}
                ignoreModified={true}
                initialValues={initialValues ? initialValues : {}}
                key={initialValues ? JSON.stringify(initialValues) : undefined}
                render={(formRenderProps) => (
                    <FormElement>
                        <div className='bg-light border p-3'>
                            <Row className='mb-6'>
                                <Col md={12}>
                                    <Field
                                        label='Dealer ID:'
                                        name='dealerId'
                                        component={DealerSelect}
                                        dealerId={initialValues ? initialValues.dealerId : 0}
                                        onDealerChanged={handleDealerChange}
                                        onDealerCleared={handleDealerClear}
                                        allowDealerZero={true}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Field
                                        label='First Name:'
                                        name='firstName'
                                        component={FormInput}
                                        maxLength={50}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Field
                                        label='Last Name:'
                                        name='lastName'
                                        component={FormInput}
                                        maxLength={50}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Field
                                        label='Phone:'
                                        name='phone'
                                        component={FormInput}
                                        maxLength={50}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Field
                                        label='Mobile Phone:'
                                        name='mobilePhone'
                                        component={FormInput}
                                        maxLength={50}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Field
                                        label='Email Address:'
                                        name='email'
                                        component={FormInput}
                                        maxLength={255}
                                    />
                                </Col>
                                <Col md={6} className='mb-3'>
                                    <Field
                                        label='IP Address:'
                                        name='ipAddress'
                                        component={FormInput}
                                        maxLength={50}
                                    />
                                </Col>
                            </Row>
                            <Row className='my-2'>
                                <Col md={12}>
                                    {formType == FormTypeEnum.Add ?
                                        <AddButton disabled={!formRenderProps.allowSubmit || loading} type='submit' /> :
                                        <DefaultButton disabled={!formRenderProps.allowSubmit || loading} variant='primary' type='submit'>Update Filter</DefaultButton>}
                                </Col>
                            </Row>
                        </div>
                    </FormElement>
                )
                }
            />
        </div>
    )
}
export default WebsiteLeadsFilterForm
